import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Visibility, Launch, Cancel } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';

interface MonitoredLegislation {
  id: number;
  number: string;
  year: string;
  title: string;
  summary?: string;
  type?: string;
  created_at_official?: string;
  url?: string;
  dou_ref?: string;
  created_at: string;
  updated_at: string;
  monitoring_started_at: string;
}

interface MonitoredLegislationsTableProps {
  agencyCode: string;
  agencyName: string;
  onViewDetails?: (legislation: MonitoredLegislation) => void;
}

export function MonitoredLegislationsTable({
  agencyCode,
  agencyName,
  onViewDetails,
}: MonitoredLegislationsTableProps) {
  const {
    data: monitoredData,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ['agency-monitored-legislations', agencyCode],
    () => apiService.getAgencyMonitoredLegislations(agencyCode),
    {
      enabled: !!agencyCode,
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    }
  );

  const handleViewDetails = (legislation: MonitoredLegislation) => {
    if (onViewDetails) {
      onViewDetails(legislation);
    }
  };

  const handleOpenUrl = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Carregando legislações monitoradas...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Erro ao carregar legislações monitoradas para a agência {agencyName}
        </Typography>
      </Alert>
    );
  }

  if (!monitoredData || monitoredData.total_monitored === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" py={4}>
        <Cancel sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Nenhuma legislação sendo monitorada para esta agência
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {agencyName} ({agencyCode})
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Legislações Monitoradas - {agencyName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {monitoredData.total_monitored} legislação(ões) sendo acompanhada(s)
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Norma
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Título
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Tipo
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Monitoramento
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Ações
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {monitoredData.legislations.map((legislation) => (
              <TableRow key={legislation.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {legislation.number}/{legislation.year}
                  </Typography>
                  {legislation.dou_ref && (
                    <Typography variant="caption" color="text.secondary">
                      DOU: {legislation.dou_ref}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 300 }}>
                    {legislation.title.length > 60
                      ? `${legislation.title.substring(0, 60)}...`
                      : legislation.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={legislation.type || 'N/A'}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    Desde {new Date(legislation.monitoring_started_at).toLocaleDateString('pt-BR')}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" gap={1} justifyContent="center">
                    {onViewDetails && (
                      <Tooltip title="Ver detalhes">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(legislation)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {legislation.url && (
                      <Tooltip title="Abrir documento">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenUrl(legislation.url!)}
                        >
                          <Launch fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
